import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Comissao } from "@/hooks/useComissoes";
import { useDentistas } from "@/hooks/useDentistas";
import { useAgendamentos } from "@/hooks/useAgendamentos";
import { useOrdemServico } from "@/hooks/useOrdemServico";
import { calcularComissao, monthInputValue, periodoDoMes, producaoDentistaCompleta } from "@/lib/performance";
import { parseLocalDate, toLocalISODate, todayLocalDate } from "@/lib/date-utils";

interface ComissaoFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: any) => Promise<void>;
  comissao?: Comissao | null;
}

const formasPagamento = [
  "Dinheiro",
  "PIX",
  "Cartão de Crédito",
  "Cartão de Débito",
  "Boleto",
  "Transferência"
];

const recebeComissao = (dentista?: { recebe_comissao?: boolean; percentual_comissao?: number } | null) =>
  !!dentista && (dentista.recebe_comissao || Number(dentista.percentual_comissao || 0) > 0);

export function ComissaoForm({ open, onOpenChange, onSubmit, comissao }: ComissaoFormProps) {
  const { dentistas } = useDentistas();
  const { agendamentos, fetchAgendamentos } = useAgendamentos();
  const { ordensServico } = useOrdemServico();
  const [modoCalculo, setModoCalculo] = useState<"mes" | "periodo">("mes");
  const [mesCalculo, setMesCalculo] = useState(monthInputValue());

  const [formData, setFormData] = useState({
    dentista_id: "",
    referencia: "",
    valor_total_procedimentos: 0,
    percentual_comissao: 0,
    valor_comissao: 0,
    data_inicio: todayLocalDate(),
    data_fim: todayLocalDate(),
    status: "Pendente" as "Pendente" | "Paga" | "Cancelada",
    data_pagamento: undefined as Date | undefined,
    forma_pagamento: "",
    observacoes: "",
  });

  useEffect(() => {
    if (comissao) {
      const inicio = parseLocalDate(comissao.data_inicio);
      setModoCalculo("periodo");
      setMesCalculo(monthInputValue(inicio));
      setFormData({
        dentista_id: comissao.dentista_id,
        referencia: comissao.referencia,
        valor_total_procedimentos: comissao.valor_total_procedimentos,
        percentual_comissao: comissao.percentual_comissao,
        valor_comissao: comissao.valor_comissao,
        data_inicio: inicio,
        data_fim: parseLocalDate(comissao.data_fim),
        status: comissao.status,
        data_pagamento: comissao.data_pagamento ? parseLocalDate(comissao.data_pagamento) : undefined,
        forma_pagamento: comissao.forma_pagamento || "",
        observacoes: comissao.observacoes || "",
      });
    } else {
      const periodo = periodoDoMes();
      setModoCalculo("mes");
      setMesCalculo(monthInputValue(periodo.inicio));
      setFormData({
        dentista_id: "",
        referencia: "",
        valor_total_procedimentos: 0,
        percentual_comissao: 0,
        valor_comissao: 0,
        data_inicio: periodo.inicio,
        data_fim: periodo.fim,
        status: "Pendente",
        data_pagamento: undefined,
        forma_pagamento: "",
        observacoes: "",
      });
    }
  }, [comissao]);

  const aplicarMesCalculo = (mes: string) => {
    setMesCalculo(mes);
    const periodo = periodoDoMes(mes);
    setFormData((prev) => ({
      ...prev,
      data_inicio: periodo.inicio,
      data_fim: periodo.fim,
      referencia: `${toLocalISODate(periodo.inicio)} a ${toLocalISODate(periodo.fim)}`,
    }));
  };

  const alterarModoCalculo = (value: "mes" | "periodo") => {
    setModoCalculo(value);
    if (value === "mes") aplicarMesCalculo(mesCalculo);
  };

  useEffect(() => {
    if (open && formData.data_inicio && formData.data_fim) {
      fetchAgendamentos(formData.data_inicio, formData.data_fim);
    }
  }, [open, formData.data_inicio, formData.data_fim]);

  useEffect(() => {
    if (!comissao && formData.dentista_id) {
      const dentista = dentistas.find((item) => item.id === formData.dentista_id);
      const producao = producaoDentistaCompleta(agendamentos, ordensServico, formData.dentista_id, formData.data_inicio, formData.data_fim);
      const percentual = recebeComissao(dentista) ? Number(dentista?.percentual_comissao || 0) : 0;
      setFormData((prev) => ({
        ...prev,
        valor_total_procedimentos: producao,
        percentual_comissao: percentual,
        referencia: prev.referencia || `${toLocalISODate(prev.data_inicio)} a ${toLocalISODate(prev.data_fim)}`,
      }));
    }
  }, [comissao, formData.dentista_id, formData.data_inicio, formData.data_fim, agendamentos, ordensServico, dentistas]);

  useEffect(() => {
    const valorComissao = calcularComissao(formData.valor_total_procedimentos, formData.percentual_comissao);
    setFormData(prev => ({ ...prev, valor_comissao: valorComissao }));
  }, [formData.valor_total_procedimentos, formData.percentual_comissao]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      data_inicio: toLocalISODate(formData.data_inicio),
      data_fim: toLocalISODate(formData.data_fim),
      data_pagamento: formData.data_pagamento ? toLocalISODate(formData.data_pagamento) : undefined,
      forma_pagamento: formData.forma_pagamento || undefined,
      observacoes: formData.observacoes || undefined,
    };

    await onSubmit(submitData);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>
            {comissao ? "Editar Comissão" : "Nova Comissão"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="dentista_id">Dentista *</Label>
              <Select value={formData.dentista_id} onValueChange={(value) => setFormData({ ...formData, dentista_id: value })} required>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o dentista" />
                </SelectTrigger>
                <SelectContent>
                  {dentistas.map((dentista) => (
                    <SelectItem key={dentista.id} value={dentista.id}>
                      {dentista.nome} - {dentista.especialidade}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="referencia">Referência *</Label>
              <Input
                id="referencia"
                value={formData.referencia}
                onChange={(e) => setFormData({ ...formData, referencia: e.target.value })}
                placeholder="Ex: Janeiro/2025"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Como deseja calcular?</Label>
              <Select value={modoCalculo} onValueChange={alterarModoCalculo}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mes">Mês inteiro</SelectItem>
                  <SelectItem value="periodo">Período personalizado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {modoCalculo === "mes" && (
              <div className="space-y-2">
                <Label>Mês de cálculo</Label>
                <Input type="month" value={mesCalculo} onChange={(event) => aplicarMesCalculo(event.target.value)} />
              </div>
            )}

            {modoCalculo === "periodo" && (
              <>
                <div className="space-y-2">
                  <Label>Período - Data Início *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.data_inicio && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.data_inicio ? format(formData.data_inicio, "PPP", { locale: ptBR }) : <span>Selecione a data</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.data_inicio}
                        onSelect={(date) => date && setFormData({ ...formData, data_inicio: date })}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Período - Data Fim *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.data_fim && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.data_fim ? format(formData.data_fim, "PPP", { locale: ptBR }) : <span>Selecione a data</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.data_fim}
                        onSelect={(date) => date && setFormData({ ...formData, data_fim: date })}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="percentual_comissao">Percentual Comissão (%) *</Label>
              <Input
                id="percentual_comissao"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.percentual_comissao}
                readOnly
                className="bg-muted"
                required
              />
              <p className="text-xs text-muted-foreground">Percentual puxado do cadastro do dentista.</p>
            </div>

            <div className="space-y-2 sm:col-span-2">
              <div className="rounded-md border bg-muted/30 p-3">
                <p className="text-sm font-medium">Cálculo automático</p>
                <div className="mt-2 grid gap-2 text-sm sm:grid-cols-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Produção realizada</p>
                    <p className="font-semibold">{formData.valor_total_procedimentos.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Percentual</p>
                    <p className="font-semibold">{formData.percentual_comissao}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Comissão</p>
                    <p className="font-semibold">{formData.valor_comissao.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                  </div>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  A produção vem dos atendimentos realizados e ordens de serviço concluídas no período. Quando o mesmo serviço estiver nos dois registros, ele entra uma única vez. O percentual vem do perfil do dentista.
                </p>
              </div>
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="status">Status *</Label>
              <Select value={formData.status} onValueChange={(value: any) => setFormData({ ...formData, status: value })} required>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pendente">Pendente</SelectItem>
                  <SelectItem value="Paga">Paga</SelectItem>
                  <SelectItem value="Cancelada">Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(formData.status === "Paga" || formData.data_pagamento) && (
              <>
                <div className="space-y-2">
                  <Label>Data de Pagamento</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.data_pagamento && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.data_pagamento ? format(formData.data_pagamento, "PPP", { locale: ptBR }) : <span>Selecione a data</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.data_pagamento}
                        onSelect={(date) => setFormData({ ...formData, data_pagamento: date })}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="forma_pagamento">Forma de Pagamento</Label>
                  <Select value={formData.forma_pagamento} onValueChange={(value) => setFormData({ ...formData, forma_pagamento: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {formasPagamento.map((forma) => (
                        <SelectItem key={forma} value={forma}>
                          {forma}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit">
              {comissao ? "Atualizar" : "Cadastrar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
